// Hardcoded Commerce rule selections streamed to the customizer in discovery mode.
//
// Mirrors discoveryTools.ts / discoveryAiTools.ts: selecting a Commerce rule in the discovery panel streams a
// payload the customizer reads to light up that rule's sandbox experience. Unlike the tool categories, a
// commerce rule carries its WHOLE configuration in the payload rather than a catalog of records.
//
// Add entries keyed by the DiscoveryModePanel item id.

// Sample Extra Options (Additional Attributes) form streamed with the Extra Options rule.
//
// This is the exact `{ options, displayPlacement }` JSON the Extra Options setup page authors (see
// pages/additionalAttributes/types.ts — AdditionalAttributeData) and the customizer's
// useAdditionalAttributesForm renders. `zakeke-customizer-react` surfaces it as the product's attributes data
// (see useZakekeAdditionalAttributes), so the form appears on a sandbox product that has no Extra Options of
// its own — and the Variants tool button appears with it, even on a product with a single variant
// (useToolsButtonsList shows that button whenever a product-page form exists).
//
// Two authoring constraints, both deliberate:
//  - `displayPlacement: 'variants-panel'` — Extra Options has two display positions and the discovery sandbox
//    demos ONE: the product (Variants) panel. The other placement ('add-to-cart-modal') opens a wizard on the
//    Add to Cart click, which collides with the test-order flow.
//  - fixed prices only. The setup page also offers `priceType: 'percentage'`, but nothing in the customizer
//    honours it — a percentage value renders and totals as if it were a flat currency amount. Keep every
//    `price` here a plain surcharge in account currency.
//
// Every field is optional (`required: false`): with the 'variants-panel' placement an unfilled REQUIRED field
// blocks the test order behind a "please fill in the required options" dialog (see the customizer's
// useAddToCart), which is not what a merchant trying the sandbox should hit.
const DISCOVERY_EXTRA_OPTIONS_FORM = {
    displayPlacement: 'variants-panel',
    options: [
        {
            id: 'discovery-gift-note',
            labelOnProduct: 'Gift card message',
            labelOnCart: 'Gift card message',
            inputType: 'text',
            required: false,
            placeholderText: 'e.g. Happy birthday, Sam!',
            max: 120,
            helpText: 'Printed on a card slipped into the package. Free of charge.',
            helpTextDisplay: 'tooltip'
        },
        {
            id: 'discovery-package-type',
            labelOnProduct: 'Packaging',
            labelOnCart: 'Packaging',
            inputType: 'dropdown',
            required: false,
            helpText: 'How the item is boxed before shipping.',
            helpTextDisplay: 'below',
            values: [
                { id: 'discovery-package-paper', label: 'Paper box (standard)', value: 'paper', price: 0, priceType: 'fixed' },
                { id: 'discovery-package-tin', label: 'Tin box', value: 'tin', price: 4.5, priceType: 'fixed' },
                { id: 'discovery-package-wood', label: 'Wooden case', value: 'wood', price: 9, priceType: 'fixed' },
                { id: 'discovery-package-fabric', label: 'Fabric pouch', value: 'fabric', price: 3, priceType: 'fixed' }
            ]
        },
        {
            id: 'discovery-extras',
            labelOnProduct: 'Add-ons',
            labelOnCart: 'Add-ons',
            inputType: 'checkbox',
            required: false,
            values: [
                { id: 'discovery-extra-keychain', label: 'Matching keychain', value: 'keychain', price: 1, priceType: 'fixed' },
                { id: 'discovery-extra-giftwrap', label: 'Gift wrapping', value: 'giftwrap', price: 2.5, priceType: 'fixed' }
            ]
        }
    ]
};

// Sample Minimum Order Quantity rule streamed with the MOQ rule.
//
// This is the RAW rule shape the customizer's data layer consumes (see zakeke-customizer-react
// interfaces/internal/product.ts — MoqRule), NOT the GraphQL shape the MOQ setup page edits. Two differences
// matter when authoring:
//  - `stepType` is numeric here (1 = fixed increment, 2 = custom packages), where the setup page uses
//    'FIXED' / 'CUSTOM'; and
//  - `customTiers` is a COMMA-SEPARATED STRING (e.g. '6,12,24,48'), not an array — the customizer splits and
//    sorts it (mapMoqRuleToQuantityRule).
//
// `zakeke-customizer-react` registers this as the product's raw `moqRules.productRule` (see
// useDiscoveryMoqRegistrar), which has top priority in computeQuantityRule — so it overrides whatever the
// sandbox product owns, and the enforced rule is re-derived by the real code path on every variant / print
// method change. That lights up the customizer's own footer quantity selector (it renders only when a quantity
// rule exists) with live snapping, plus the add-to-cart block below the minimum.
//
// We demo the FIXED variant (minimum 12, then multiples of 6 → 12, 18, 24 …): it shows the floor AND the
// stepping in one rule, and keeps the footer control a stepper, which reads as more obviously interactive than
// the dropdown that custom packages produce. Custom packages are described on the pill but not streamed; to try
// them instead, set `stepType: 2` and a `customTiers` string (`minimumQuantity` is then ignored — the effective
// minimum becomes the lowest tier).
//
// The bookkeeping fields the real API sends are present but inert: the sandbox rule is never persisted, and
// `moqRuleID` uses a negative sentinel so it can never collide with a real account rule. `colors` /
// `printTypes` / `products` stay empty because the registrar only honours the product-level rule — variant and
// print-method rules are keyed by real account ids a hardcoded payload cannot know.
const DISCOVERY_MOQ_RULE = {
    moqRuleID: -777,
    name: 'Discovery sample MOQ',
    minimumQuantity: 12,
    stepType: 1,
    fixedStep: 6,
    customTiers: '',
    // 1 = validate the order total (2 would validate per bulk row/cell, which needs a bulk product).
    targetRuleType: 1,
    colors: [],
    printTypes: [],
    products: [],
    sellerID: 0
};

// Sample Bulk Variation Form configuration streamed with the Bulk Variation Forms rule.
//
// `zakeke-customizer-react` registers these as the product's bulk configuration (see
// useDiscoveryBulkRegistrar), which lights up the bulk grid on the Add to Cart click. The whole
// BO-test-preview bulk pipeline is host-independent already: the customizer synthesizes the
// `{ variants, attributes }` a storefront would answer with, prices each row through the normal test-mode
// pricing (resolved locally, no ecommerce round-trip), and re-prices the grid in place when the merchant edits
// the test base price. So this works on ANY sandbox product, including a single-variant one with no attributes
// at all.
//
// The grid AXES are not authored here: they are derived from the previewed product, so the merchant sees a
// bulk form over their own variants (see useDiscoveryBulkRegistrar -> resolveSandboxAxes). The product's real
// axes are taken in API order, skipping any that offer only one value (nothing to vary); two of them give the
// paired matrix, one gives the row list.
//
// `maxGridCells` caps the matrix: two axes are used only if their combination fits within it, otherwise the
// first axis alone is shown. This exists because a real apparel product can be 10 colors x 8 sizes = 80 cells,
// which does not fit the backoffice preview iframe and reads as broken. It is a LAYOUT budget, not a
// performance one — test-mode prices resolve locally in the customizer, so a wide grid is unusable, not slow.
//
// `fallbackAxes` is the last resort, used only when the product has nothing to vary (a single variant, or
// variant attributes that are all single-valued). Those names are DELIBERATELY generic so they do not match a
// real Zakeke attribute id (matching only strips spaces and `+`, so 'Size' never collides with e.g. 'pa_size'):
// the customizer then renders them as placeholder axes. Two notes on how a placeholder looks:
//  - the name doubles as the column HEADER (the customizer emits `{ id: name, label: name }`), which is why
//    these are human-readable words and not slugs;
//  - its VALUES are always 'Option A/B/C'. They come from a single hardcoded list in the customizer, shared
//    between the axis values and the synthetic variant combinations — that shared source is what lets a
//    merchant-entered row match a valid combination, so it is not authorable from here.
// One name gives a 3-row list; adding a second gives a 3x3 placeholder matrix (9 cells, so inside any sane
// `maxGridCells`).
//
// `showDropdownVersion` / `showUnitPrice` are account-level bulk display settings (whatever the API returns
// for an account with no bulk configuration). They are pinned so the sandbox always shows the grid version
// with per-unit and row prices, which is also what makes the test-price interaction visible.
const DISCOVERY_BULK_FORM = {
    fallbackAxes: ['Size'],
    maxGridCells: 36,
    showDropdownVersion: false,
    showUnitPrice: true
};

// Profanity Filter & Image Moderator is EXPLAINER-ONLY, so its payload is a bare marker with no configuration:
// the customizer recognises it and opens a descriptive dialog (useDiscoveryExplainerDialogs) instead of a live
// sandbox. It cannot be demoed for real, in either half:
//  - both the text check and the image check are one server call (`profanityFilterCheck`) keyed by a rule the
//    sandbox account does not own — and the rule itself carries the `apiKey` of an EXTERNAL moderation service,
//    so the word dictionary is not Zakeke's to hand to the browser (no REST or GraphQL endpoint returns it);
//  - image moderation is an AI vision judgement, which has no client-side equivalent at all.
// A mocked verdict would therefore be an invented result presented as the product's, so we describe the rule
// instead of faking one.
export const DISCOVERY_COMMERCE_PAYLOADS: Record<string, object> = {
    'extra-options': { rule: 'extra-options', attributes: DISCOVERY_EXTRA_OPTIONS_FORM },
    'profanity-filter': { rule: 'profanity-filter' },
    'minimum-order-quantity': {
        rule: 'minimum-order-quantity',
        moqRules: { productRule: DISCOVERY_MOQ_RULE, colorRules: null, printTypeRules: null }
    },
    'bulk-variation-forms': { rule: 'bulk-variation-forms', bulk: DISCOVERY_BULK_FORM }
};
